"use client";

import { Users, Brain, Info, ClipboardCheck, Rocket, ArrowLeft } from "lucide-react";
import { GlowingEffect } from "@/components/ui/glowing-effect";
import { cn } from "@/lib/utils";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useLanguage } from "@/context/LanguageContext";

export default function LeadershipRoles() {
    const { t } = useLanguage();

    return (
        <div className="min-h-screen">
            {/* SEO: Localized metadata targeting "Agile Leadership" and "Heilbronn" */}
            <SEO
                title={t("seo.leadership.landing.title")}
                description={t("seo.leadership.landing.description")}
                keywords={t("seo.leadership.landing.keywords").split(",").map(k => k.trim())}
            />
            <main className="container mx-auto px-4 py-12">
                <Breadcrumbs />

                <h1 className="text-4xl font-bold mb-8">Agile Leadership Roles</h1>

                <ul className="grid grid-cols-1 grid-rows-none gap-4 md:grid-cols-12 md:grid-rows-3 lg:gap-4 xl:max-h-[34rem] xl:grid-rows-2">
                    {/* Tile 1: 9 Roles (Top Left) */}
                    <GridItem
                        area="md:[grid-area:1/1/2/7] xl:[grid-area:1/1/2/5]"
                        icon={<Users className="h-4 w-4" />}
                        title="The 9 Leadership Roles"
                        description="Explore the different archetypes that drive successful teams."
                        href="/tools/leadership_roles/roles"
                    />

                    {/* Tile 2: Theoretical Framework (Bottom Left on XL) */}
                    <GridItem
                        area="md:[grid-area:1/7/2/13] xl:[grid-area:2/1/3/5]"
                        icon={<Brain className="h-4 w-4" />}
                        title="Theoretical Framework"
                        description="Daniel Pink (Motivation) meets Simone Spiegler (Agile Maturity)."
                        href="/tools/leadership_roles/model"
                    />

                    {/* Tile 3: How to Achieve Self-Organization (Middle Big Tall on XL) */}
                    <GridItem
                        area="md:[grid-area:2/1/3/7] xl:[grid-area:1/5/3/8]"
                        icon={<Info className="h-4 w-4" />}
                        title="How to achieve self-organization"
                        description="From Agile Coach to fully self-organizing teams."
                        href="/tools/leadership_roles/about"
                    />

                    {/* Tile 4: Competence Interview (Top Right on XL) */}
                    <GridItem
                        area="md:[grid-area:2/7/3/13] xl:[grid-area:1/8/2/13]"
                        icon={<ClipboardCheck className="h-4 w-4" />}
                        title="Agile Coach Competence Test"
                        description="Scenario-based assessment for action competence."
                        href="/tools/leadership_roles/interview"
                    />

                    {/* Tile 5: Startups (Bottom Right on XL) */}
                    <GridItem
                        area="md:[grid-area:3/1/4/13] xl:[grid-area:2/8/3/13]"
                        icon={<Rocket className="h-4 w-4" />}
                        title="Startups Context"
                        description="Applying these roles in founder teams."
                        href="/tools/leadership_roles/perspectives"
                    />
                </ul>
            </main>
            <Footer />
        </div>
    );
}

interface GridItemProps {
    area: string;
    icon: React.ReactNode;
    title: string;
    description: React.ReactNode;
    href: string;
}

const GridItem = ({ area, icon, title, description, href }: GridItemProps) => {
    return (
        <li className={cn("min-h-[14rem] list-none", area)}>
            <Link to={href} className="block h-full">
                <div className="relative h-full rounded-[1.25rem] border-[0.75px] border-border p-2 md:rounded-[1.5rem] md:p-3 cursor-pointer hover:border-primary/50 transition-colors group">
                    <GlowingEffect
                        spread={40}
                        glow={true}
                        disabled={false}
                        proximity={64}
                        inactiveZone={0.01}
                        borderWidth={3}
                    />
                    <div className="relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-xl border-[0.75px] bg-background p-6 shadow-sm dark:shadow-[0px_0px_27px_0px_rgba(45,45,45,0.3)] md:p-6 group-hover:bg-muted/5 transition-colors">
                        <div className="relative flex flex-1 flex-col justify-between gap-3">
                            <div className="w-fit rounded-lg border-[0.75px] border-border bg-muted p-2">
                                {icon}
                            </div>
                            <div className="space-y-3">
                                <h3 className="pt-0.5 text-xl leading-[1.375rem] font-semibold font-sans tracking-[-0.04em] md:text-2xl md:leading-[1.875rem] text-balance text-foreground">
                                    {title}
                                </h3>
                                <h2 className="[&_b]:md:font-semibold [&_strong]:md:font-semibold font-sans text-sm leading-[1.125rem] md:text-base md:leading-[1.375rem] text-muted-foreground">
                                    {description}
                                </h2>
                            </div>
                        </div>
                    </div>
                </div>
            </Link>
        </li>
    );
};
